import { observer } from 'mobx-react-lite'
import React, { useContext } from 'react'
import { BoxArrowRight, Files, Trash, Eye, EyeSlash } from 'react-bootstrap-icons'
import { useParams } from 'react-router-dom'
import { CampaignContext } from '../../../../../contexts/CampaignContext'
import { toastError, toastSuccess } from '../../../../FormComponents/Toasts'
import UpdatableField from '../../../../FormComponents/UpdatableField'
import PortalKebabMenu from '../../../../Shared/TimelineParts/PortalKebabMenu'
import BudgetDetailsPanel from './BudgetDetailsPanel'
import { useReadOnly } from '../../../../../contexts/ReadOnlyContext'

export default observer(function BudgetTableSection({ campaignScenario, canDelete, isFirst }) {
  const { id } = useParams()
  const readOnly = useReadOnly()
  const { duplicateCampaignScenario, deleteCampaignScenario, toggleCampaignScenarioActive } = useContext(CampaignContext)

  const exportScenario = () => {
    window.open(`/api/project_manager/campaigns/${id}/campaign_scenarios/${campaignScenario.id}/export`)
  }

  const handleToggleActive = () => {
    toggleCampaignScenarioActive(campaignScenario.id)
      .then(() => toastSuccess(campaignScenario.active ? 'Budget table activated' : 'Budget table deactivated'))
      .catch(() => {})
  }

  const handleDuplicate = () => {
    duplicateCampaignScenario(campaignScenario.id)
      .then(() => toastSuccess('Budget table duplicated'))
      .catch(() => {})
  }

  const handleDelete = () => {
    if (!canDelete) return
    if (!window.confirm(`Delete "${campaignScenario.name}"? This cannot be undone.`)) return
    deleteCampaignScenario(campaignScenario.id)
      .then(() => toastSuccess('Budget table deleted'))
      .catch(() => {})
  }

  const menuItems = [
    { key: 'duplicate', label: 'Duplicate', icon: <Files />, onClick: handleDuplicate },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Trash />,
      onClick: handleDelete,
      disabled: !canDelete,
      title: canDelete ? undefined : 'Cannot delete the only budget table',
    },
  ]

  return (
    <div className={isFirst ? '' : 'mt-6 pt-6 border-t-1 border-border-primary dark:border-rb-dark-mode-lighter'}>
      <div className={`flex items-center justify-between ${campaignScenario.active ? 'mb-4' : ''}`}>
        <div className="flex items-center min-w-0">
          <UpdatableField
            url={`/api/project_manager/campaigns/${id}/campaign_scenarios/${campaignScenario.id}`}
            model="campaign_scenario"
            attribute="name"
            className="font-bold text-lg dark:text-white"
            onFieldUpdate={updated => { campaignScenario.name = updated.name }}
          >
            {campaignScenario.name}
          </UpdatableField>
          {!campaignScenario.active && (
            <span className="ml-3 text-xs font-semibold uppercase px-2 py-1 rounded-full bg-gray-100 dark:bg-rb-dark-mode-lighter text-gray-500 dark:text-gray-400">
              Off
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleToggleActive}
            disabled={readOnly}
            className={`transition-all p-2 rounded-md dark:text-rb-dark-mode ${readOnly ? 'cursor-default' : ''} ${campaignScenario.active ? 'bg-rb-green text-white' : 'bg-gray-100 dark:bg-rb-dark-mode-lighter text-gray-500 dark:text-gray-300'}`}
            title={campaignScenario.active ? 'Deactivate budget table' : 'Activate budget table'}
          >
            {campaignScenario.active ? <Eye /> : <EyeSlash />}
          </button>
          <button
            type="button"
            className="hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter text-black dark:text-white transition-all p-2 rounded-md"
            onClick={exportScenario}
            title="Export"
          >
            <BoxArrowRight />
          </button>
          {!readOnly && <PortalKebabMenu items={menuItems} ariaLabel="Budget table options" />}
        </div>
      </div>
      {campaignScenario.active && <BudgetDetailsPanel campaignScenario={campaignScenario} />}
    </div>
  )
})
